import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import { red } from '@mui/material/colors';
import Dialog from '@mui/material/Dialog';
import React from 'react';
import { withTranslation } from 'react-i18next';

import { dispatchSiteAssociationActions } from '@sophocles/api/odlApp';
import { fetchRadioLookup } from '@sophocles/entities/Radio';
import { addToast } from '@sophocles/features/toast';
import { unshareVirtualAccess } from '@sophocles/features/virtual/api';

class DeactivateStructureDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      radioAction: 'inventory',
      error: false,
      submitError: null,
      submitting: false,
      warnings: [],
    };
  }

  componentDidMount() {
    const { site, dispatch } = this.props;
    const iccid = site.getIn(['relay_controller', 'radio', 'iccid'], null);
    const mac = site.getIn(['relay_controller', 'radio', 'mac_address'], null);
    const identifier = iccid ?? mac;

    if (!identifier || !dispatch) return;

    dispatch(fetchRadioLookup(identifier)).then(lookupResponse => {
      if (!lookupResponse) return;
      const warnings = [];
      const prepaid = lookupResponse.prepaid_coverage;
      if (prepaid) {
        const { t } = this.props;
        warnings.push({
          severity: 'info',
          message: t('sites.infoPrepaidDeactivate', {
            endDate: prepaid.end_date,
            ownerName: prepaid.owner_name || t('general.unknown'),
          }),
        });
      }
      if (warnings.length > 0) {
        this.setState({ warnings });
      }
    });
  }

  submit = () => {
    const { site, siteGroup, router, isVirtual, dispatch, t } = this.props;
    const { radioAction } = this.state;
    const hasDevice = site.get('relay_controller', null) != null;

    // Only require radioAction if the site has a device
    if (!isVirtual && hasDevice && !radioAction) {
      this.setState({ error: true });
      return;
    }

    const siteId = site.get('id', 0);
    const sgId = siteGroup.get('id', 0);
    const customerId = siteGroup.get('customer', 0);

    if (isVirtual) {
      // Virtual unshare stays on the legacy thunk — the associations unshare action
      // requires params.membership.id, which this dialog does not have readily
      // available. TODO(ENG): migrate once an associations-unshare path that can
      // resolve the membership id is wired in.
      this.setState({ submitting: true, submitError: null });
      dispatch(unshareVirtualAccess([{ site_id: siteId, third_party_id: customerId }]))
        .then(() => router.navigate(`/c/${customerId}/plant/${sgId}/structures`, { replace: true }))
        .catch(() => {
          this.setState({ submitting: false, submitError: 'submitError' });
          dispatch(
            addToast({
              title: t('general.error', 'Error'),
              message: t('general.errorOccurred', 'An error occurred. Please try again.'),
              level: 'error',
              dismissible: true,
            }),
          );
        });
    } else {
      // Use radioAction if device exists, otherwise default to 'inventory'.
      const action = hasDevice ? radioAction : 'inventory';
      this.setState({ submitting: true, submitError: null });
      dispatchSiteAssociationActions(siteId, [{ key: 'deactivate', params: { radio_action: action } }])
        .then(() => router.navigate(`/c/${customerId}/plant/${sgId}/structures`, { replace: true }))
        .catch(() => this.setState({ submitting: false, submitError: 'submitError' }));
    }
  };

  handleRadioActionChange = event => {
    this.setState({ radioAction: event.target.value, error: false });
  };

  render() {
    const { open, closeDialog, isBillboard, site, siteGroup, t } = this.props;
    const { radioAction, error, submitError, submitting, warnings } = this.state;

    const isVirtual = siteGroup.get('is_virtual', true);
    const hasDevice = site.get('relay_controller', null) != null;

    const actions = [
      <Button key="cancel" variant="outlined" onClick={closeDialog} disabled={submitting}>
        {t('actions.cancel')}
      </Button>,
      <Button key="delete" variant="contained" color="error" onClick={this.submit} disabled={submitting}>
        {isVirtual ? t('actions.unshare') : t('actions.delete')}
      </Button>,
    ];

    return (
      <Dialog open={open} titleStyle={{ color: 'white', backgroundColor: red[500] }} onClose={closeDialog}>
        <DialogTitle>{isBillboard ? t('sites.deactivateStructure') : t('sites.deactivateSite')}</DialogTitle>
        <DialogContent dividers>
          <div>
            {warnings.length > 0 &&
              warnings.map((warning, index) => {
                const isObject = typeof warning === 'object' && warning !== null;
                const message = isObject ? warning.message : warning;
                const severity = isObject ? warning.severity || 'warning' : 'warning';
                const icon = severity === 'info' ? <InfoOutlinedIcon /> : <WarningAmberIcon />;
                return (
                  <Alert key={index} severity={severity} icon={icon} sx={{ mb: 2 }}>
                    <Typography variant="body2">{message}</Typography>
                  </Alert>
                );
              })}
            <div>
              {isVirtual
                ? t('sites.siteUnshareConfirmation', {
                    siteTerm: t('sites.site'),
                  })
                : t('sites.siteDeletionConfirmation', {
                    siteTerm: t('sites.site'),
                  })}
            </div>
            <div>
              <span>
                <b>{t('general.name')}:</b> {site.get('name', '')}
              </span>
            </div>
            <div>
              <span>
                <b>{t('general.description')}:</b> {site.get('description', '')}
              </span>
            </div>
            <div>
              <span>
                <b>{t('general.location')}:</b> {site.get('location_description', '')}
              </span>
            </div>
            {/* Device Deactivation Options - only show if site has a device */}
            {!isVirtual && hasDevice && (
              <FormControl component="fieldset" error={error} style={{ marginTop: '20px' }}>
                <FormLabel component="legend">{t('sites.whatShouldHappenToExistingUnit')}</FormLabel>
                <RadioGroup value={radioAction} onChange={this.handleRadioActionChange}>
                  <FormControlLabel
                    value="inventory"
                    control={<Radio />}
                    label={
                      <div>
                        <b>{t('sites.returnToInventory')}</b> -{' '}
                        <span dangerouslySetInnerHTML={{ __html: t('sites.returnToInventoryDescription') }} />
                      </div>
                    }
                  />

                  <FormControlLabel
                    value="purge"
                    control={<Radio />}
                    label={
                      <div>
                        <b>{t('sites.retire')}</b> -{' '}
                        <span dangerouslySetInnerHTML={{ __html: t('sites.retireDescription') }} />
                      </div>
                    }
                  />
                </RadioGroup>
                {error && (
                  <div style={{ color: red[500], marginTop: '10px' }}>
                    {t('sites.selectOptionBeforeProceeding', 'Please select an option before proceeding.')}
                  </div>
                )}
              </FormControl>
            )}
            {submitError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  {t('general.errorOccurred', 'An error occurred. Please try again.')}
                </Typography>
              </Alert>
            )}
          </div>
        </DialogContent>
        <DialogActions>{actions}</DialogActions>
      </Dialog>
    );
  }
}

export default withTranslation()(DeactivateStructureDialog);
